import StakeholderCombobox from "@app/components/stakeholder-combobox";
import TxnTablePanel, {
  BulkAction,
  useTxnSort,
} from "@app/components/txn-table-panel";
import type { TxnSortColumn } from "@app/components/txn-table-panel";
import { getPerspectiveId } from "@app/lib/perspective";
import { environment } from "@app/relay/relay-environment";
import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  commitMutation,
  fetchQuery,
  graphql,
  usePreloadedQuery,
} from "react-relay";
import type { PreloadedQuery } from "react-relay";

import type { ruleDetailPage_Query } from "@app/queries/ruleDetailPage_Query.graphql";
import type { ruleDetailPage_UpdateRuleMutation } from "@app/queries/ruleDetailPage_UpdateRuleMutation.graphql";
import type { ruleDetailPage_ExcludeTxnMutation } from "@app/queries/ruleDetailPage_ExcludeTxnMutation.graphql";
import type { ruleDetailPage_CarveOutMutation } from "@app/queries/ruleDetailPage_CarveOutMutation.graphql";
import type { ruleDetailPage_SplitRuleMutation } from "@app/queries/ruleDetailPage_SplitRuleMutation.graphql";
import type { ruleDetailPage_SubruleCreateMutation } from "@app/queries/ruleDetailPage_SubruleCreateMutation.graphql";
import type { ruleDetailPage_SubruleUpdateMutation } from "@app/queries/ruleDetailPage_SubruleUpdateMutation.graphql";
import type { ruleDetailPage_SubruleDeleteMutation } from "@app/queries/ruleDetailPage_SubruleDeleteMutation.graphql";

import RecaptureDialog from "@app/pages/rules/components/recapture-dialog";
import type { Delta, DeltaRow } from "@app/pages/rules/components/recapture-dialog";
import SplitDialog from "@app/pages/rules/components/split-dialog";
import SubruleEditor from "@app/pages/rules/components/subrule-editor";
import type { SubruleTarget } from "@app/pages/rules/components/subrule-editor";
import StakeholderTxnsDialog from "@app/pages/categorize/stakeholder-txns-dialog";

export const ruleDetailQuery = graphql`
  query ruleDetailPage_Query($ruleId: ID!, $perspectiveId: ID) {
    ruleDetail(ruleId: $ruleId, perspectiveId: $perspectiveId) {
      rule {
        id
        regex
        matchKind
        direction
        source
        matchesCount
        ownedCount
        targetType
        stakeholder {
          id
          name
        }
        expenseNature {
          id
          name
        }
        isCaptureRule
        captureLiteralPrefix
        subrules {
          id
          valuePattern
          matchKind
          priority
          enabled
          stakeholder {
            id
            name
          }
          destinationBankAccount {
            id
            name
          }
        }
        captureResolutions {
          value
          count
          stakeholder {
            id
            name
          }
          destinationBankAccount {
            id
            name
          }
        }
      }
      totalCount
      limit
      txns {
        txn {
          id
        }
        md5
        postingDate
        description
        expenseNature {
          id
          name
        }
        amount
        kind
        accountNumber
        internal
        classification
        category
        counterparty {
          id
          name
        }
      }
    }
    allStakeholders {
      id
      name
    }
    bankAccounts {
      id
      name
      nickname
      last4
    }
  }
`;

const updateRuleMutation = graphql`
  mutation ruleDetailPage_UpdateRuleMutation($input: UpdateRuleInputObject!) {
    updateRule(input: $input) {
      rule {
        id
        stakeholder {
          id
          name
        }
      }
      errors {
        message
      }
    }
  }
`;

const excludeTxnMutation = graphql`
  mutation ruleDetailPage_ExcludeTxnMutation(
    $input: RuleExcludeTxnInputObject!
  ) {
    ruleExcludeTxn(input: $input) {
      rule {
        id
      }
      errors {
        message
      }
    }
  }
`;

const carveOutMutation = graphql`
  mutation ruleDetailPage_CarveOutMutation($input: RuleCarveOutInputObject!) {
    ruleCarveOut(input: $input) {
      rule {
        id
      }
      errors {
        message
      }
    }
  }
`;

const splitRuleMutation = graphql`
  mutation ruleDetailPage_SplitRuleMutation($input: SplitRuleInputObject!) {
    splitRule(input: $input) {
      rules {
        id
      }
      errors {
        message
      }
    }
  }
`;

const subruleCreateMutation = graphql`
  mutation ruleDetailPage_SubruleCreateMutation($input: RuleSubruleCreateInputObject!) {
    ruleSubruleCreate(input: $input) {
      rule { id }
      errors { message }
    }
  }
`;

const subruleUpdateMutation = graphql`
  mutation ruleDetailPage_SubruleUpdateMutation($input: RuleSubruleUpdateInputObject!) {
    ruleSubruleUpdate(input: $input) {
      rule { id }
      errors { message }
    }
  }
`;

const subruleDeleteMutation = graphql`
  mutation ruleDetailPage_SubruleDeleteMutation($input: RuleSubruleDeleteInputObject!) {
    ruleSubruleDelete(input: $input) {
      rule { id }
      errors { message }
    }
  }
`;

// One matching-transaction row, as the shared shell renders it (it satisfies
// TxnTableRow). The page also reads these rows by id for the synthesized delta
// and the split-dialog mapping — one source, no second query.
type Row =
  ruleDetailPage_Query["response"]["ruleDetail"]["txns"][number];

// Translate the shell's sort column into a comparator over the bounded matching
// set. The shell renders all the standard headers; we sort client-side (this is
// the already-fetched, capped preview — the export does the full server sort).
const compareRows = (column: TxnSortColumn) => (a: Row, b: Row): number => {
  switch (column) {
    case "AMOUNT":
      return a.amount - b.amount || a.md5.localeCompare(b.md5);
    case "DESCRIPTION":
      return (
        a.description.localeCompare(b.description, undefined, { sensitivity: "base" }) ||
        a.md5.localeCompare(b.md5)
      );
    case "ACCOUNT":
      return (
        (a.accountNumber ?? "").localeCompare(b.accountNumber ?? "") ||
        a.md5.localeCompare(b.md5)
      );
    case "COUNTERPARTY":
      return (
        (a.counterparty?.name ?? "~").localeCompare(b.counterparty?.name ?? "~") ||
        a.md5.localeCompare(b.md5)
      );
    case "CATEGORY":
      return (a.category ?? "~").localeCompare(b.category ?? "~") || a.md5.localeCompare(b.md5);
    case "NATURE":
      return (
        (a.expenseNature?.name ?? "~").localeCompare(b.expenseNature?.name ?? "~") ||
        a.md5.localeCompare(b.md5)
      );
    default:
      return a.postingDate.localeCompare(b.postingDate) || a.md5.localeCompare(b.md5);
  }
};

export default function RuleDetailPage({
  queryRef,
}: {
  queryRef: PreloadedQuery<ruleDetailPage_Query>;
}) {
  const preloaded = usePreloadedQuery(ruleDetailQuery, queryRef);
  const [data, setData] = useState(preloaded);
  useEffect(() => setData(preloaded), [preloaded]);

  const [picking, setPicking] = useState(false);
  const [pendingTarget, setPendingTarget] = useState<{
    stakeholderId?: string;
    name?: string;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshInPlace = () => {
    fetchQuery<ruleDetailPage_Query>(
      environment,
      ruleDetailQuery,
      queryRef.variables
    )
      .toPromise()
      .then((fresh) => fresh && setData(fresh));
  };

  const detail = data.ruleDetail;
  const rule = detail.rule;
  const allStakeholders = data.allStakeholders;
  const accountOptions = data.bankAccounts.map((a) => ({
    id: a.id,
    name: a.name,
    nickname: a.nickname,
    last4: a.last4,
  }));

  const onPick = (choice: { stakeholderId?: string; name?: string }) => {
    setPicking(false);
    setError(null);
    setPendingTarget(choice);
  };

  const commit = (scope: "OWNED" | "ALL_MATCHING") => {
    if (!pendingTarget) return;
    setSaving(true);
    setError(null);
    const target = pendingTarget.stakeholderId
      ? { stakeholderId: pendingTarget.stakeholderId }
      : { stakeholderName: pendingTarget.name };
    commitMutation<ruleDetailPage_UpdateRuleMutation>(environment, {
      mutation: updateRuleMutation,
      variables: {
        input: { ruleId: rule.id, ...target, reattributeScope: scope },
      },
      onCompleted: (response) => {
        setSaving(false);
        const payload = response.updateRule;
        if (payload?.rule) {
          setPendingTarget(null);
          refreshInPlace();
        } else {
          setError(payload?.errors?.[0]?.message ?? "Could not save");
        }
      },
      onError: (err) => {
        setSaving(false);
        setError(err.message);
      },
    });
  };

  // ---- sort (client-side over the shell's bounded matching set) -----------
  const { sort, direction, onSort, sorting } = useTxnSort("DATE", "DESC");
  const sortedTxns = useMemo(() => {
    const rows = [...detail.txns].sort(compareRows(sort));
    return direction === "ASC" ? rows : rows.reverse();
  }, [detail.txns, sort, direction]);

  // ---- exclude / carve confirm (routes through the shared preview dialog) --
  // No backend exclude/carve preview resolver exists; the affected rows are the
  // ones we already hold, so we synthesize the dropped-from-this-rule delta.
  type Affecting = {
    kind: "exclude" | "carve";
    txnIds: string[];
    target?: { stakeholderId?: string; name?: string };
    // The shell owns selection; a carve initiated from the bulk bar carries its
    // clear callback so the confirm can drop the selection on success.
    clearSelection?: () => void;
  };
  const [affecting, setAffecting] = useState<Affecting | null>(null);
  const [dialogSaving, setDialogSaving] = useState(false);

  const toDeltaRow = (id: string): DeltaRow | null => {
    const t = detail.txns.find((row) => row.txn.id === id);
    if (!t) return null;
    return {
      txn: { id },
      postingDate: t.postingDate,
      description: t.description,
      amount: t.amount,
      accountNumber: t.accountNumber ?? null,
      expenseNature: t.expenseNature,
    };
  };

  const affectingDelta: Delta | null = affecting
    ? (() => {
        const rows = affecting.txnIds
          .map(toDeltaRow)
          .filter((r): r is DeltaRow => r !== null);
        return {
          capturedCount: 0,
          droppedCount: rows.length,
          manualConflictCount: 0,
          truncated: false,
          captured: [],
          droppedRuleAssigned: rows,
          droppedManual: [],
        };
      })()
    : null;

  const perspectiveId = getPerspectiveId() ?? undefined;

  const confirmAffecting = () => {
    if (!affecting) return;
    setDialogSaving(true);
    const onError = (err: Error) => {
      setDialogSaving(false);
      window.alert(err.message);
    };
    const onDone = () => {
      setDialogSaving(false);
      affecting.clearSelection?.();
      setAffecting(null);
      refreshInPlace();
    };
    if (affecting.kind === "exclude") {
      commitMutation<ruleDetailPage_ExcludeTxnMutation>(environment, {
        mutation: excludeTxnMutation,
        variables: {
          input: { ruleId: rule.id, txnId: affecting.txnIds[0], perspectiveId },
        },
        onCompleted: (res) => {
          if (res.ruleExcludeTxn?.errors.length) {
            onError(new Error(res.ruleExcludeTxn.errors[0].message));
            return;
          }
          onDone();
        },
        onError,
      });
    } else {
      commitMutation<ruleDetailPage_CarveOutMutation>(environment, {
        mutation: carveOutMutation,
        variables: {
          input: {
            parentRuleId: rule.id,
            txnIds: affecting.txnIds,
            stakeholderId: affecting.target?.stakeholderId,
            stakeholderName: affecting.target?.name,
            perspectiveId,
          },
        },
        onCompleted: (res) => {
          if (res.ruleCarveOut?.errors.length) {
            onError(new Error(res.ruleCarveOut.errors[0].message));
            return;
          }
          onDone();
        },
        onError,
      });
    }
  };

  // ---- split -------------------------------------------------------------
  const [splitting, setSplitting] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitError, setSplitError] = useState<string | null>(null);

  const confirmSplit = (keptTxnIds: string[]) => {
    setSplitting(true);
    setSplitError(null);
    commitMutation<ruleDetailPage_SplitRuleMutation>(environment, {
      mutation: splitRuleMutation,
      // Split by example: the kept side becomes group A, the rest group B; the
      // backend synthesizes both (indexed) predicates from the selection.
      variables: { input: { ruleId: rule.id, selectedTxnIds: keptTxnIds } },
      onCompleted: (res) => {
        setSplitting(false);
        if (res.splitRule?.errors.length) {
          setSplitError(res.splitRule.errors[0].message);
          return;
        }
        setSplitOpen(false);
        refreshInPlace();
      },
      onError: (err) => {
        setSplitting(false);
        setSplitError(err.message);
      },
    });
  };

  // ---- capture subrules --------------------------------------------------
  const [subruleSaving, setSubruleSaving] = useState(false);

  const runSubruleMutation = <T extends { readonly variables: object; readonly response: object }>(
    mutation: Parameters<typeof commitMutation>[1]["mutation"],
    input: object,
    errorsOf: (res: T["response"]) => ReadonlyArray<{ readonly message: string }> | undefined
  ) => {
    setSubruleSaving(true);
    commitMutation<T>(environment, {
      mutation,
      variables: { input } as T["variables"],
      onCompleted: (res) => {
        setSubruleSaving(false);
        const errs = errorsOf(res);
        if (errs && errs.length) {
          window.alert(errs[0].message);
          return;
        }
        refreshInPlace();
      },
      onError: (err) => {
        setSubruleSaving(false);
        window.alert(err.message);
      },
    });
  };

  const createSubrule = (value: string, matchKind: string, target: SubruleTarget) =>
    runSubruleMutation<ruleDetailPage_SubruleCreateMutation>(
      subruleCreateMutation,
      { parentRuleId: rule.id, valuePattern: value, matchKind, perspectiveId, ...target },
      (res) => res.ruleSubruleCreate?.errors
    );

  const updateSubrule = (subruleId: string, target: SubruleTarget) =>
    runSubruleMutation<ruleDetailPage_SubruleUpdateMutation>(
      subruleUpdateMutation,
      { subruleId, perspectiveId, ...target },
      (res) => res.ruleSubruleUpdate?.errors
    );

  const deleteSubrule = (subruleId: string) =>
    runSubruleMutation<ruleDetailPage_SubruleDeleteMutation>(
      subruleDeleteMutation,
      { subruleId, perspectiveId },
      (res) => res.ruleSubruleDelete?.errors
    );

  // ---- advanced inspector ------------------------------------------------
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Drill into the rule's counterparty's full transactions dialog.
  const [txnsDialogOpen, setTxnsDialogOpen] = useState(false);

  const carveSelected = (
    txnIds: string[],
    target: { stakeholderId?: string; name?: string },
    clearSelection: () => void
  ) => setAffecting({ kind: "carve", txnIds, target, clearSelection });

  const exportParams: Record<string, string> = {
    rule_id: rule.id,
    sort,
    sort_direction: direction,
  };
  if (perspectiveId) exportParams.perspective_id = perspectiveId;

  const targetLabel = pendingTarget?.stakeholderId
    ? allStakeholders.find((s) => s.id === pendingTarget.stakeholderId)?.name ??
      "the selected counterparty"
    : pendingTarget?.name
    ? `new counterparty "${pendingTarget.name}"`
    : "";

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      <Link to="/rules" className="text-sm text-gray-500 hover:underline">
        ← all rules
      </Link>

      <div className="bg-white rounded border p-4 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-400 uppercase">
            {rule.direction === "ANY"
              ? "regex"
              : `${rule.direction.toLowerCase()} regex`}
          </span>
          <code className="font-mono text-sm bg-gray-50 rounded px-2 py-1">
            {rule.regex}
          </code>
          <span className="text-xs text-gray-400">
            {rule.source.toLowerCase()} · {rule.matchesCount} matches
          </span>
        </div>
        <div className="flex items-center gap-2 text-sm relative">
          <span className="text-gray-500">
            {rule.targetType === "NATURE" ? "tags as" : "assigns to"}
          </span>
          {rule.targetType === "NATURE" ? (
            <span className="font-medium">{rule.expenseNature?.name ?? "—"}</span>
          ) : (
            <button
              type="button"
              onClick={() => setPicking(!picking)}
              className="font-medium hover:underline"
            >
              {rule.stakeholder?.name ?? "—"}
            </button>
          )}
          {rule.stakeholder && (
            <button
              type="button"
              onClick={() => setTxnsDialogOpen(true)}
              title="Open this counterparty's transactions dialog"
              className="text-gray-300 hover:text-gray-600"
            >
              ⤢
            </button>
          )}
          <span className="text-xs text-gray-400">
            — changing this re-points the matching transactions below
          </span>
          <button
            type="button"
            onClick={() => setSplitOpen(true)}
            className="ml-auto px-2 py-1 text-xs rounded border hover:bg-gray-50"
          >
            Split this rule…
          </button>
          {picking && (
            <div className="absolute z-20 top-full left-0 mt-1 bg-white rounded border shadow-lg p-2">
              <StakeholderCombobox options={allStakeholders} onPick={onPick} />
            </div>
          )}
        </div>

        <div>
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="text-xs text-gray-400 hover:text-gray-700"
          >
            {showAdvanced ? "▾ Advanced" : "▸ Advanced"}
          </button>
          {showAdvanced && (
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
              <span>
                match kind:{" "}
                <span className="font-mono text-gray-700">
                  {rule.matchKind}
                </span>
              </span>
              <span>
                regex:{" "}
                <code className="font-mono bg-gray-50 rounded px-1 py-0.5 text-gray-700 break-all">
                  {rule.regex}
                </code>
              </span>
              <span>
                priority direction:{" "}
                <span className="text-gray-700">{rule.direction}</span>
              </span>
            </div>
          )}
        </div>
      </div>

      {(rule.isCaptureRule ||
        (rule.captureLiteralPrefix != null && rule.captureLiteralPrefix !== "")) && (
        <SubruleEditor
          captureLiteralPrefix={rule.captureLiteralPrefix ?? null}
          fallbackName={rule.stakeholder?.name ?? null}
          subrules={rule.subrules}
          resolutions={rule.captureResolutions}
          stakeholders={allStakeholders}
          accounts={accountOptions}
          saving={subruleSaving}
          onCreate={createSubrule}
          onUpdate={updateSubrule}
          onDelete={deleteSubrule}
        />
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <TxnTablePanel<Row>
          mode="inline"
          interaction="editable"
          title="Matching transactions"
          totalCount={detail.totalCount}
          rows={sortedTxns}
          emptyLabel="No transactions match this rule's regex."
          sort={sort}
          direction={direction}
          onSort={onSort} sorting={sorting}
          exportParams={exportParams}
          busy={dialogSaving}
          extraColumns={[
            {
              key: "remove",
              header: "",
              align: "right",
              render: (row) => (
                <button
                  type="button"
                  onClick={() =>
                    setAffecting({ kind: "exclude", txnIds: [row.txn.id] })
                  }
                  className="text-xs text-red-300 hover:text-red-600 whitespace-nowrap"
                >
                  Remove from this rule
                </button>
              ),
            },
          ]}
          renderBulkActions={(selectedIds, clearSelection) => (
            <BulkAction label="Carve out selected → stricter rule" disabled={dialogSaving}>
              {(close) => (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500">
                    Target for the carved rule (defaults to this rule&apos;s):
                  </p>
                  <StakeholderCombobox
                    options={allStakeholders}
                    onPick={(choice) => {
                      close();
                      carveSelected(selectedIds, choice, clearSelection);
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      close();
                      carveSelected(selectedIds, {}, clearSelection);
                    }}
                    className="text-xs text-indigo-700 hover:underline"
                  >
                    Keep this rule&apos;s target
                  </button>
                </div>
              )}
            </BulkAction>
          )}
        />
      </div>

      {pendingTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center"
          onClick={() => !saving && setPendingTarget(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl p-5 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="font-semibold mb-1">
              Re-point transactions for this rule
            </h2>
            <p className="text-sm text-gray-600 mb-3">
              This rule matches{" "}
              <span className="font-medium">{rule.matchesCount}</span>{" "}
              transaction{rule.matchesCount === 1 ? "" : "s"}.{" "}
              <span className="font-medium">{rule.ownedCount}</span>{" "}
              {rule.ownedCount === 1 ? "is" : "are"} currently attributed to{" "}
              <span className="font-medium">{rule.stakeholder?.name ?? "—"}</span>{" "}
              by this rule. New target:{" "}
              <span className="font-medium">{targetLabel}</span>.
            </p>
            {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
            <div className="space-y-2">
              <button
                type="button"
                disabled={saving}
                onClick={() => commit("OWNED")}
                className="block w-full text-left rounded border px-3 py-2 hover:bg-gray-50 disabled:opacity-50"
              >
                <span className="font-medium">
                  Re-point only this rule's {rule.ownedCount}
                </span>
                <span className="block text-xs text-gray-500">
                  Leaves manual edits and transactions on other counterparties
                  untouched.
                </span>
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => commit("ALL_MATCHING")}
                className="block w-full text-left rounded border border-amber-300 px-3 py-2 hover:bg-amber-50 disabled:opacity-50"
              >
                <span className="font-medium">
                  Re-point all {rule.matchesCount} matching
                </span>
                <span className="block text-xs text-gray-500">
                  Overrides manual edits and reassigns transactions currently on
                  other counterparties.
                </span>
              </button>
            </div>
            <button
              type="button"
              disabled={saving}
              onClick={() => setPendingTarget(null)}
              className="mt-3 text-sm text-gray-500 hover:text-gray-900 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {affecting && (
        <RecaptureDialog
          title={
            affecting.kind === "exclude"
              ? "Remove this transaction from the rule"
              : "Carve these transactions into a stricter rule"
          }
          confirmLabel={
            affecting.kind === "exclude" ? "Remove" : "Carve out"
          }
          delta={affectingDelta}
          loading={false}
          saving={dialogSaving}
          overrideManual={false}
          onToggleOverride={() => {}}
          onConfirm={confirmAffecting}
          onCancel={() => setAffecting(null)}
        />
      )}

      {splitOpen && (
        <SplitDialog
          txns={detail.txns.map((t) => ({
            id: t.txn.id,
            postingDate: t.postingDate,
            description: t.description,
            amount: t.amount,
            expenseNature: t.expenseNature,
          }))}
          saving={splitting}
          error={splitError}
          onConfirm={confirmSplit}
          onCancel={() => {
            setSplitOpen(false);
            setSplitError(null);
          }}
        />
      )}

      {txnsDialogOpen && rule.stakeholder && (
        <StakeholderTxnsDialog
          stakeholder={{ id: rule.stakeholder.id, name: rule.stakeholder.name }}
          perspectiveId={getPerspectiveId() ?? null}
          onClose={() => setTxnsDialogOpen(false)}
          onChanged={refreshInPlace}
        />
      )}
    </div>
  );
}
